/* eslint-disable */
import { defineStore, storeToRefs } from 'pinia';
import { CONFIGURATION } from '@/enum';
import { MessageCenterService } from '@/services';
import { DocumentsInterface, FileInterface, MessageCenterStoreInterface } from '@/types';
import {
  ChatInterface,
  Configuration,
  FileTypes,
  AttachmentFileConfig,
  MessageInterface,
  SyncDataMessage,
  RecipientData,
} from '@/types/MessageCenter';
import { useAuthStore } from '@/store';

export default defineStore('MessageCenterStore', {
  state: (): MessageCenterStoreInterface => {
    return {
      visible: false,
      loading: false,
      loadingFile: false,
      attachmentFile: undefined,
      screen: 'list-chats',
      error: undefined,
      currentChatId: undefined,
      chats: {} as Record<number, ChatInterface>,
      messages: {} as Record<number, MessageInterface>,
      messageText: '',
      messagesPage: 1,
      hasMoreMessages: true,
      recipients: [] as RecipientData[],
      configuration: {
        configurations: [] as Configuration[],
        file_types: [] as FileTypes[],
      } as AttachmentFileConfig,
    };
  },

  actions: {
    catchError(e: string) {
      this.error = e;
    },

    async getChats() {
      this.loading = true;
      this.chats = {};

      const response = await MessageCenterService.getChats().catch(this.catchError);
      const chats = response?.data?.data;

      if (chats) {
        for (const chat of chats) {
          this.chats[chat.id] = chat;
        }
      }

      this.loading = false;
    },

    async deleteChat(id: number) {
      this.loading = true;
      const response = await MessageCenterService.deleteChat(id).catch(this.catchError);
      delete this.chats[id];
      this.loading = false;
    },

    async getAvailableMembers(search: string) {
      this.loading = true;
      const response = await MessageCenterService.getAvailableMembers(search).catch(this.catchError);
      this.recipients = response?.data.data;
      this.loading = false;
    },

    async createNewChat(payload: FormData) {
      this.loading = true;
      const response = await MessageCenterService.createNewChat(payload).catch(this.catchError);
      if (response?.data?.data) {
        const chat = response.data.data;
        this.chats[chat.id] = chat;
      }
      this.loading = false;
    },

    async setActiveChat(id: number) {
      this.loading = true;
      this.currentChatId = id;
      this.messages = {};
      this.messagesPage = 1;
      this.hasMoreMessages = true;

      const response = await MessageCenterService.getMessages(this.currentChatId, this.messagesPage).catch(
        this.catchError,
      );
      const messages = response?.data?.data;
      const meta = response?.data?.meta;

      if (messages) {
        for (const message of messages) {
          this.messages[message.id] = message;
        }
        if (meta.current_page < meta.last_page) {
          this.messagesPage += 1;
        } else {
          this.hasMoreMessages = false;
        }
      }

      const chat = this.chats[this.currentChatId];
      if (chat) {
        chat.unread_count = 0;
      }

      this.loading = false;
      this.screen = 'chat';
    },

    async getMoreMessages() {
      if (!this.currentChatId || this.loading || !this.hasMoreMessages) return;
      this.loading = true;

      const response = await MessageCenterService.getMessages(this.currentChatId, this.messagesPage);
      const newMessages = response.data.data;
      const meta = response?.data?.meta;

      for (const message of newMessages) {
        this.messages[message.id] = message;
      }
      if (meta.current_page < meta.last_page) {
        this.messagesPage += 1;
      } else {
        this.hasMoreMessages = false;
      }
      this.loading = false;
    },

    async sendMessage(message: FormData) {
      if (!this.currentChatId) return;
      this.loading = true;

      const response = await MessageCenterService.sendMessage(this.currentChatId, message).catch(this.catchError);

      if (response?.data.data) {
        this.syncChat(response.data.data.chat);
        const message = response.data.data.message;
        this.messages[message.id] = message;
        this.attachmentFile = undefined;
      }
      this.loading = false;
    },

    async deleteMessage(messageId: number) {
      if (!this.currentChatId) return;
      this.loading = true;
      try {
        await MessageCenterService.deleteMessage(messageId);
        delete this.messages[messageId];
      } catch (error: any) {
        this.doToast(error.data.message, 'Error', true);
      } finally {
        this.loading = false;
      }
    },

    async deleteMessageFile(messageId: number, documentId: string) {
      if (!this.currentChatId) return;
      this.loading = true;

      await MessageCenterService.deleteMessageFile(messageId, documentId).catch(this.catchError);

      const msg = this.messages?.[messageId];
      if (!msg) {
        this.loading = false;
        return;
      }

      msg.attachments = msg.attachments?.filter((a) => a?.id !== documentId);

      if (msg.attachments?.length === 0 && msg.content === null) await this.deleteMessage(msg.id);

      this.loading = false;
    },

    async syncDataMessage(data: SyncDataMessage) {
      this.syncChat(data.chat);

      if (this.currentChatId === data.chat.id) {
        this.messages[data.message.id] = data.message;
      }
    },

    async chatCreated(chat: ChatInterface) {
      this.chats[chat.id] = chat;
    },

    async chatDeleted(chat: ChatInterface) {
      delete this.chats[chat.id];
    },

    async messageDeleted(data: SyncDataMessage) {
      delete this.messages[data.message.id];
      this.syncChat(data.chat);
    },

    async messageFileDeleted(data: { message: MessageInterface; file: DocumentsInterface }) {
      const message = this.messages[data.message.id];
      if (!message || !message.attachments) return;

      message.attachments = message.attachments.filter((attachment) => Number(attachment.id) !== data.file.id);
    },

    async syncChat(chat: ChatInterface) {
      this.chats = {
        ...this.chats,
        [chat.id]: { ...this.chats[chat.id], ...chat },
      };
    },

    async getMessageSettings() {
      this.loading = true;
      const response = await MessageCenterService.getMessageSettings().catch(this.catchError);
      if (response?.data?.data) {
        this.configuration = response.data.data;
      }
      this.loading = false;
    },

    async updateMessageSettings(payload: AttachmentFileConfig) {
      this.loading = true;
      const response = await MessageCenterService.updateMessageSettings(payload).catch(this.catchError);
      this.configuration = response?.data.data;
      this.loading = false;
    },

    async loadMessageFile(messageId: number, file: FileInterface): Promise<void> {
      this.loadingFile = true;
      this.attachmentFile = undefined;
      const response = await MessageCenterService.downloadAttachmentStream(messageId, file.id).catch(this.catchError);

      const mimeType = (response?.headers?.['content-type'] as string) || 'application/octet-stream';

      if (!response?.data) {
        this.loadingFile = false;
        throw new Error('Failed to load file');
      }

      const blob =
        (response.data as Blob).type === mimeType
          ? response.data
          : (response.data as Blob).slice(0, (response.data as Blob).size, mimeType);

      this.attachmentFile = {
        name: file.name,
        type: mimeType,
        size: blob.size,
        content: blob,
      };

      this.loadingFile = false;
    },
  },

  getters: {
    isYou: (state) => {
      const { user } = storeToRefs(useAuthStore());
      return (sender: { id: number }) => sender.id === user?.value?.id;
    },
    sortedChats(state: MessageCenterStoreInterface): ChatInterface[] {
      return Object.values(state.chats).sort((a, b) => {
        if (a.updated_at && b.updated_at) {
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        }
        return 0;
      });
    },

    sortedMessages(state: MessageCenterStoreInterface): MessageInterface[] {
      return Object.values(state.messages).sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    },

    hasUnreadChats(state: MessageCenterStoreInterface): boolean {
      return Object.values(state.chats).some((chat) => chat.unread_count > 0);
    },

    getCurrentChat(state: MessageCenterStoreInterface): ChatInterface | undefined {
      const id = state.currentChatId;
      return id !== undefined ? state.chats[id] : undefined;
    },

    getFileTypes(state: MessageCenterStoreInterface): FileTypes[] {
      return state.configuration.file_types.filter((type) => type.allowed) || [];
    },

    getFileConfigurations(state: MessageCenterStoreInterface): { max_upload_size: number; max_files: number } {
      const maxUploadSize = state.configuration.configurations.find(
        (config) => config.name == CONFIGURATION.MAX_UPLOAD_SIZE,
      );
      const maxFiles = state.configuration.configurations.find((config) => config.name == CONFIGURATION.MAX_FILES);

      return {
        max_upload_size: Number(maxUploadSize?.value) || 1024,
        max_files: Number(maxFiles?.value) || 0,
      };
    },
  },
});
